/** Sign-in, sign-up, forgotten password, and the e-mail links (verify, reset). Shown by TokenGate
 * whenever there is no session. Copy says what happens; errors come from the server's fixed texts. */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { api, ApiError } from "@/api/client";
import { focusOnMount } from "@/components/ui";
import * as S from "@/api/schemas";
import { z } from "zod";

type Mode = "signin" | "register" | "forgot" | "token" | "request";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: { sitekey: string; callback: (t: string) => void }) => string;
      reset: (id?: string) => void;
    };
  }
}

/** Cloudflare's bot check, loaded only when the server has a site key. */
function Turnstile({
  siteKey,
  onToken,
  resetKey = 0,
}: {
  siteKey: string;
  onToken: (t: string) => void;
  /** bump after a refused attempt: a check answer is good for one request only (G3 M4) */
  resetKey?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const widget = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (resetKey > 0 && window.turnstile && widget.current !== undefined)
      window.turnstile.reset(widget.current);
  }, [resetKey]);
  useEffect(() => {
    const render = () => {
      if (ref.current && window.turnstile && !ref.current.dataset.done) {
        ref.current.dataset.done = "1";
        widget.current = window.turnstile.render(ref.current, { sitekey: siteKey, callback: onToken });
      }
    };
    if (window.turnstile) return render();
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.onload = render;
    document.head.appendChild(s);
  }, [siteKey, onToken]);
  return <div ref={ref} />;
}

export function Shell({ title, lead, children }: { title: string; lead?: string; children: ReactNode }) {
  return (
    <main className="gate">
      <div className="gate-inner">
        <p className="gate-brand">
          <img src="/logo.svg" alt="Bason" width={87} height={100} />
        </p>
        <div className="card stack">
          <div>
            <h1>{title}</h1>
            {lead && <p className="muted">{lead}</p>}
          </div>
          {children}
        </div>
        <p className="faint center legal-links">
          <a href="/help">How it works</a> · <a href="/legal/terms">Terms</a> ·{" "}
          <a href="/legal/privacy">Privacy</a> · <a href="/legal/health">Health data</a>
        </p>
      </div>
    </main>
  );
}

/** "Continue with Google": the invite (if any) and the browser's time zone go along. */
/** The terms at sign-up (D-40): one sentence next to the button it names, the links bold and clickable (new tab, so
 * the form stays filled in). The server records the acceptance with its version and time (`consents.terms`). */
export function TermsLine({ action }: { action: string }) {
  return (
    <p className="terms-line">
      By {action}, you agree to our{" "}
      <a href="/legal/terms" target="_blank" rel="noopener">
        <strong>Terms of use</strong>
        <span className="sr-only"> (opens in a new tab)</span>
      </a>{" "}
      and{" "}
      <a href="/legal/privacy" target="_blank" rel="noopener">
        <strong>Privacy policy</strong>
        <span className="sr-only"> (opens in a new tab)</span>
      </a>
      .
    </p>
  );
}

export function googleStartUrl(invite: string, tz: string): string {
  const q = new URLSearchParams();
  if (invite) q.set("invite", invite);
  if (tz) q.set("tz", tz);
  const s = q.toString();
  return `/api/v1/auth/google/start${s ? `?${s}` : ""}`;
}

/** The sign-in refusal for an address that isn't confirmed yet (then "Send a new link" is offered). */
export const isUnconfirmed = (msg: string | null) => !!msg && /confirm your e-mail/i.test(msg);

/** "Send a new link": asks for a new confirmation e-mail. The answer is the same for every address. */
function ResendLink({ email }: { email: string }) {
  const [state, setState] = useState<"idle" | "busy" | "sent" | "error">("idle");
  const [msg, setMsg] = useState<string | null>(null);
  if (state === "sent")
    return (
      <p role="status" style={{ margin: 0 }}>
        If {email || "that address"} has an account waiting, a new link is on its way. Check your spam folder
        too.
      </p>
    );
  return (
    <div className="stack" style={{ gap: 6 }}>
      <button
        type="button"
        disabled={state === "busy" || !email}
        onClick={() => {
          setState("busy");
          api("/auth/verify/resend", S.AuthStatus, { token: null, body: { email } }).then(
            () => setState("sent"),
            (e: unknown) => {
              setMsg(errorText(e));
              setState("error");
            },
          );
        }}
      >
        Send a new link
      </button>
      {msg && (
        <div className="badge bad" role="alert">
          {msg}
        </div>
      )}
    </div>
  );
}

function errorText(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return "Something went wrong. Check your connection and try again.";
}

function timezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

const URL_ERRORS: Record<string, string> = {
  google: "Google sign-in didn't work. Try again, or use your e-mail.",
  google_cancelled: "Google sign-in was cancelled.",
  invite: "You need an invite to join the beta. Use your invite link, or ask for one.",
  suspended: "This account is suspended. Contact the person who invited you.",
  too_many: "Too many sign-in attempts from here. Wait a few minutes and try again.",
};

export function AuthScreen({ tokenForm, onSignedIn }: { tokenForm: ReactNode; onSignedIn: () => void }) {
  const params = new URLSearchParams(window.location.search);
  const invite = params.get("invite") ?? "";
  const [mode, setMode] = useState<Mode>(
    window.location.pathname.startsWith("/register") || invite ? "register" : "signin",
  );
  const [cfg, setCfg] = useState<S.AuthConfig | null>(null);
  // a personal invite link carries the address in its fragment (never sent to the server)
  const [email, setEmail] = useState(
    () => new URLSearchParams(window.location.hash.slice(1)).get("email") ?? "",
  );
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [race, setRace] = useState(""); // "Your race" on the invite request (not the name!)
  const [code, setCode] = useState(invite);
  const [more, setMore] = useState(false);
  const deleted = params.get("deleted") === "1";
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaRound, setCaptchaRound] = useState(0);
  const [error, setError] = useState<string | null>(URL_ERRORS[params.get("error") ?? ""] ?? null);
  const [sent, setSent] = useState<string | null>(null);
  const [sentTitle, setSentTitle] = useState("Check your e-mail");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api("/auth/config", S.AuthConfig, { token: null }).then(setCfg, () => setCfg(null));
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === "signin") {
        await api("/auth/login", S.AuthStatus, {
          token: null,
          body: { email, password, turnstile: captcha },
        });
        onSignedIn();
      } else if (mode === "register") {
        const r = await api("/auth/register", S.AuthStatus, {
          token: null,
          body: { email, password, invite: code || null, name, timezone: timezone(), turnstile: captcha },
        });
        if (r.status === "signed_in") return onSignedIn(); // a personal invite for this address: no e-mail step
        setSent(`We sent a confirmation link to ${email}. Open it to finish signing up.`);
      } else if (mode === "request") {
        await api("/invite-requests", z.object({ status: z.string() }), {
          token: null,
          body: { email, note: race, turnstile: captcha },
        });
        setSentTitle("Request received");
        setSent(
          `Thanks! We open the beta in small groups. When it's your turn, you get an invite link at ${email}.`,
        );
      } else if (mode === "forgot") {
        await api("/auth/password/forgot", S.AuthStatus, {
          token: null,
          body: { email, turnstile: captcha },
        });
        setSent(`If ${email} has an account, a reset link is on its way (valid 30 minutes).`);
      }
    } catch (err) {
      setError(errorText(err));
      if (captcha) {
        setCaptcha(null); // used up by this attempt: a fresh check for the next one
        setCaptchaRound((n) => n + 1);
      }
    } finally {
      setBusy(false);
    }
  }

  const google = cfg?.google && mode !== "forgot" && mode !== "token";
  const titles: Record<Mode, string> = {
    signin: "Sign in",
    register: "Create your account",
    forgot: "Reset your password",
    token: "Sign in with an access token",
    request: "Request an invite",
  };
  if (sent)
    return (
      <Shell title={sentTitle}>
        <p role="status">{sent}</p>
        <button
          onClick={() => {
            setSent(null);
            setSentTitle("Check your e-mail");
            setMode("signin");
          }}
        >
          Back to sign in
        </button>
      </Shell>
    );
  return (
    <Shell
      title={titles[mode]}
      lead={
        mode === "register"
          ? "Bason plans your training for your race and adapts it every day. The beta needs an invite."
          : mode === "request"
            ? "Training for an ultra, a bikepacking race or a gravel event? The beta is free and opens in small groups."
            : mode === "signin"
              ? "Plans for endurance racers that adapt every day: Garmin, routes and an AI coach."
              : undefined
      }
    >
      {deleted && mode === "signin" && (
        <p className="badge good" role="status">
          Your account was deleted, with all its data.
        </p>
      )}
      {mode === "token" ? (
        tokenForm
      ) : (
        <form className="stack" onSubmit={submit} aria-label={titles[mode]}>
          {google && (
            <>
              <a className="btn" href={googleStartUrl(code, timezone())}>
                Continue with Google
              </a>
              <TermsLine action="continuing with Google" />
              <p className="muted center">or with your e-mail</p>
            </>
          )}
          {mode === "request" && (
            <label>
              Your race (optional)
              <input
                value={race}
                onChange={(e) => setRace(e.target.value)}
                maxLength={300}
                placeholder="e.g. Silk Road Mountain Race 2027"
              />
            </label>
          )}
          {mode === "register" && (
            <label>
              Your name
              <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
            </label>
          )}
          <label>
            E-mail
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              name="email"
              autoComplete="username"
              autoFocus
            />
          </label>
          {mode !== "forgot" && mode !== "request" && (
            <label>
              Password
              <input
                type="password"
                required
                minLength={mode === "register" ? 10 : 1}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === "register" ? "new-password" : "current-password"}
              />
              {mode === "register" && (
                <small className="faint">At least 10 characters; a short sentence works well.</small>
              )}
            </label>
          )}
          {mode === "register" && cfg?.signup !== "open" && (
            <label>
              Invite code
              <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} required />
            </label>
          )}
          {cfg?.turnstile_site_key && (
            <Turnstile siteKey={cfg.turnstile_site_key} onToken={setCaptcha} resetKey={captchaRound} />
          )}
          {error && (
            <div className="badge bad" role="alert">
              {error}
            </div>
          )}
          {mode === "signin" && isUnconfirmed(error) && <ResendLink email={email} />}
          <button className="primary" type="submit" disabled={busy}>
            {busy
              ? "Please wait…"
              : mode === "signin"
                ? "Sign in"
                : mode === "register"
                  ? "Create account"
                  : mode === "request"
                    ? "Request an invite"
                    : "Send reset link"}
          </button>
          {mode === "register" && <TermsLine action="clicking Create account" />}
        </form>
      )}
      <div className="row" style={{ gap: 12, flexWrap: "wrap" }}>
        {mode !== "signin" && (
          <button className="link" onClick={() => setMode("signin")}>
            Sign in
          </button>
        )}
        {mode !== "register" && cfg?.signup !== "closed" && (
          <button className="link" onClick={() => setMode("register")}>
            I have an invite
          </button>
        )}
        {mode !== "request" && cfg?.signup === "invite" && (
          <button className="link" onClick={() => setMode("request")}>
            Request an invite
          </button>
        )}
        {mode === "signin" && (
          <button className="link" onClick={() => setMode("forgot")}>
            Forgot password
          </button>
        )}
        {mode !== "token" && !more && (
          <button className="link faint-link" onClick={() => setMore(true)} aria-expanded={false}>
            More options
          </button>
        )}
        {mode !== "token" && more && (
          // the "More options" button it replaces had the focus: hand it on, not back to the page (G3)
          <button className="link" onClick={() => setMode("token")} ref={focusOnMount}>
            Use an access token
          </button>
        )}
      </div>
    </Shell>
  );
}

/** /verify?token=… and /reset?token=…: the links from our e-mails. */
export function EmailLink({ kind, onSignedIn }: { kind: "verify" | "reset"; onSignedIn: () => void }) {
  const query = new URLSearchParams(window.location.search);
  const token = query.get("token") ?? "";
  const confirmed = query.get("confirmed") === "1";
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const done = () => {
    window.history.replaceState(null, "", "/");
    onSignedIn();
  };
  useEffect(() => {
    if (kind !== "verify") return;
    api("/auth/verify", S.AuthStatus, { token: null, body: { token } }).then(
      (r) => {
        // opened in another browser than the one that signed up: the address is confirmed, now choose a password
        if (r.status === "choose_password" && r.token)
          window.location.replace(`/reset?token=${encodeURIComponent(r.token)}&confirmed=1`);
        else done();
      },
      (e: unknown) => setError(errorText(e)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (kind === "verify")
    return (
      <Shell title="Confirming your e-mail">
        {error ? (
          <div className="badge bad" role="alert">
            {error}
          </div>
        ) : (
          <p className="muted">One moment…</p>
        )}
        {error && (
          <>
            <label>
              Your e-mail
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </label>
            <ResendLink email={email} />
            <a href="/login">Back to sign in</a>
          </>
        )}
      </Shell>
    );
  return (
    <Shell
      title={confirmed ? "Choose your password" : "Choose a new password"}
      lead={confirmed ? "Your e-mail is confirmed. Choose your password to finish." : undefined}
    >
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            await api("/auth/password/reset", S.AuthStatus, { token: null, body: { token, password } });
            done();
          } catch (err) {
            setError(errorText(err));
          } finally {
            setBusy(false);
          }
        }}
      >
        <label>
          New password
          <input
            type="password"
            required
            minLength={10}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            autoFocus
          />
        </label>
        {error && (
          <div className="badge bad" role="alert">
            {error}
          </div>
        )}
        <button className="primary" type="submit" disabled={busy}>
          Save and sign in
        </button>
      </form>
    </Shell>
  );
}
